import { useState, useEffect, useRef, useCallback } from 'react'
import { submitScore, fetchLeaderboard, type GameScore, type LeaderboardData } from '../../api/games'

export default function SolGamesPanel() {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [leaderboard, setLeaderboard] = useState<LeaderboardData | null>(null)
  const [lastScore, setLastScore] = useState<GameScore | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const loadLeaderboard = useCallback(() => {
    fetchLeaderboard().then(setLeaderboard).catch(console.warn)
  }, [])

  useEffect(() => {
    loadLeaderboard()
  }, [loadLeaderboard])

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const t = e.data?.type
      if (t === 'gameOver' || t === 'gameWin' || t === 'levelComplete') {
        setSubmitting(true)
        submitScore({
          gameName: 'captain_sol',
          score: e.data.score,
          solCollected: e.data.solCollected,
          levelReached: e.data.levelReached,
        })
          .then(saved => {
            setLastScore(saved)
            loadLeaderboard()
          })
          .catch(console.warn)
          .finally(() => setSubmitting(false))
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [loadLeaderboard])

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Game */}
      <div className="flex-1 bg-surface-card rounded-xl border border-fringe border-spectral p-3 flex flex-col items-center">
        <h2 className="text-sm font-semibold text-ink mb-2">Captain SOL</h2>
        <iframe
          ref={iframeRef}
          src="/games/stackman_comic/index.html"
          className="w-full rounded-lg border border-fringe-strong"
          style={{ height: '500px', background: '#050311' }}
          title="Captain SOL"
        />
        {lastScore && (
          <div className="mt-2 text-xs text-ink-muted">
            Last run: {lastScore.solCollected} SOL collected | Level {lastScore.levelReached}
            {submitting && ' (saving...)'}
          </div>
        )}
      </div>

      {/* Leaderboard */}
      <div className="w-full lg:w-80 bg-surface-card rounded-xl border border-fringe border-spectral p-3">
        <h3 className="text-sm font-semibold text-ink mb-3">Leaderboard</h3>
        {leaderboard?.myBest && leaderboard.myBest.playedAt && (
          <div className="mb-3 p-2 rounded-lg bg-accent-purple/10 border border-accent-purple/20">
            <div className="text-[10px] text-ink-muted uppercase tracking-wider mb-1">Your Best</div>
            <div className="flex items-center gap-3">
              <div>
                <div className="text-sm font-semibold text-ink">{leaderboard.myBest.solCollected} SOL</div>
              </div>
              <div className="text-xs text-ink-muted">Level {leaderboard.myBest.levelReached}</div>
              <div className="text-xs text-gray-500">Score {leaderboard.myBest.score}</div>
            </div>
          </div>
        )}
        {/* Column headers */}
        <div className="flex items-center gap-2 px-2 py-1 text-[9px] text-gray-400 uppercase tracking-wider border-b border-fringe mb-1">
          <span className="w-5 text-center">#</span>
          <span className="flex-1">Player</span>
          <span className="w-12 text-right">SOL</span>
          <span className="w-10 text-right">Level</span>
        </div>
        <div className="space-y-1 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-surface">
          {leaderboard?.scores.map((s, i) => (
            <div key={s.id ?? i} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-surface-hover text-xs">
              <span className={`w-5 text-center font-bold ${i < 3 ? 'text-accent-yellow' : 'text-gray-500'}`}>
                {i + 1}
              </span>
              <span className="flex-1 text-ink-soft truncate">
                {s.portfolioId.slice(0, 4)}...{s.portfolioId.slice(-4)}
              </span>
              <span className="w-12 text-right text-ink font-medium">{s.solCollected}</span>
              <span className="w-10 text-right text-gray-500">L{s.levelReached}</span>
            </div>
          ))}
          {(!leaderboard?.scores || leaderboard.scores.length === 0) && (
            <div className="text-xs text-gray-500 text-center py-4">No scores yet. Be the first!</div>
          )}
        </div>
      </div>
    </div>
  )
}
